<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const props = defineProps({
    currentPage: {
      type: Number,
      required: true,
      default: 1
    },
    totalItems: {
      type: Number,
      required: true,
      default: 0
    },
    itemsPerPage: {
      type: Number,
      required: true,
      default: 10
    },
    maxPagesToShow: {
      type: Number,
      default: 12
    }
  });

  const emit = defineEmits(['page-change']);

  const totalPages = computed(() => {
    return Math.ceil(props.totalItems / props.itemsPerPage);
  });

  const pagesList = computed(() => {
    return Array.from({ length: totalPages.value }, (_, i) => i + 1);
  });

  const cappedPagesList = computed(() => {
    const totalPgs = totalPages.value;
    const current = props.currentPage;
    if (totalPgs <= props.maxPagesToShow) return pagesList.value;

    let start = Math.max(1, current - Math.floor(props.maxPagesToShow / 2));
    let end = start + props.maxPagesToShow - 1;
    if (end > totalPgs) {
      end = totalPgs;
      start = end - props.maxPagesToShow + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
      emit('page-change', page);
    }
  };
</script>

<template>
  <nav aria-label="Page navigation" v-if="pagesList.length > 1">
    <ul class="pagination justify-content-center">
      <li
        class="page-item"
        :class="{ disabled: currentPage === 1 }"
        v-if="pagesList.length > maxPagesToShow && currentPage > 1"
        @click.prevent="goToPage(1)"
      >
        <a class="page-link" href="#" tabindex="-1">
          <i class="bi bi-skip-start-fill" aria-hidden="true"></i>
          <span class="sr-only">{{ t('components.pagination.first') }}</span>
        </a>
      </li>
      <li
        class="page-item"
        :class="{ disabled: currentPage === 1 }"
        v-if="currentPage > 1"
        @click.prevent="goToPage(currentPage - 1)"
      >
        <a class="page-link" href="#" tabindex="-1">
          <i class="bi bi-caret-left-fill" aria-hidden="true"></i>
          <span class="sr-only">{{ t('components.pagination.previous') }}</span>
        </a>
      </li>
      <li
        v-for="page in cappedPagesList"
        :key="page"
        class="page-item"
        :class="{ active: currentPage === page }"
        @click.prevent="goToPage(page)"
      >
        <a class="page-link" href="#">{{ page }}</a>
      </li>
      <li
        class="page-item"
        :class="{ disabled: currentPage === totalPages }"
        v-if="currentPage < totalPages"
        @click.prevent="goToPage(currentPage + 1)"
      >
        <a class="page-link" href="#">
          <i class="bi bi-caret-right-fill" aria-hidden="true"></i>
          <span class="sr-only">{{ t('components.pagination.next') }}</span>
        </a>
      </li>
      <li
        class="page-item"
        :class="{ disabled: currentPage === totalPages }"
        v-if="pagesList.length > maxPagesToShow && currentPage < totalPages"
        @click.prevent="goToPage(totalPages)"
      >
        <a class="page-link" href="#">
          <i class="bi bi-skip-end-fill" aria-hidden="true"></i>
          <span class="sr-only">{{ t('components.pagination.last') }}</span>
        </a>
      </li>
    </ul>
  </nav>
</template>

<style scoped lang="scss">
  .pagination {
    margin-bottom: 1rem;
  }

  .page-link {
    cursor: pointer;
  }
</style>
